/**
 * Chat Input Component
 * Input field for sending messages with voice input support
 */

import React, { useState, useEffect, useRef } from 'react';
import './ChatInput.css';
import { Send, Mic, MicOff } from 'lucide-react';

const ChatInput = ({ onSend, disabled, placeholder }) => {
  const [message, setMessage] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [isSupported, setIsSupported] = useState(false);
  const recognitionRef = useRef(null);
  const messageRef = useRef('');
  const lastFinalTranscriptRef = useRef('');
  const voiceCommittedRef = useRef('');
  const isRecordingRef = useRef(false);

  // Update messageRef when message changes
  useEffect(() => {
    messageRef.current = message;
  }, [message]);

  // Check if speech recognition is supported
  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SpeechRecognition) {
      setIsSupported(true);
      const recognition = new SpeechRecognition();
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = 'en-US';

      recognition.onstart = () => {
        console.log('Speech recognition started');
        isRecordingRef.current = true;
        setIsRecording(true);
      };

      recognition.onresult = (event) => {
        let interimTranscript = '';
        let finalTranscript = '';

        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalTranscript += transcript + ' ';
          } else {
            interimTranscript += transcript;
          }
        }

        if (finalTranscript) {
          const newFinal = finalTranscript.trim();
          const lastFinal = lastFinalTranscriptRef.current;
          // Only append the *new* part to avoid repeating the same phrase (API often returns cumulative finals)
          const toAppend = lastFinal && newFinal.startsWith(lastFinal)
            ? newFinal.slice(lastFinal.length).trim()
            : newFinal;
          lastFinalTranscriptRef.current = newFinal;
          if (toAppend) {
            voiceCommittedRef.current = (voiceCommittedRef.current + ' ' + toAppend).trim();
            setMessage(voiceCommittedRef.current);
            messageRef.current = voiceCommittedRef.current;
          }
        } else if (interimTranscript) {
          const newMessage = voiceCommittedRef.current ? voiceCommittedRef.current + ' ' + interimTranscript : interimTranscript;
          setMessage(newMessage);
          messageRef.current = newMessage;
        }
      };

      recognition.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        if (event.error === 'no-speech' || event.error === 'audio-capture') {
          // Don't stop on these errors, just log them
          console.log('Speech recognition error (non-fatal):', event.error);
        } else {
          isRecordingRef.current = false;
          setIsRecording(false);
        }
      };

      recognition.onend = () => {
        if (isRecordingRef.current) {
          console.log('Speech recognition ended, restarting...');
          try {
            recognition.start();
          } catch (error) {
            console.log('Could not restart recognition:', error);
            isRecordingRef.current = false;
            setIsRecording(false);
          }
        } else {
          isRecordingRef.current = false;
          setIsRecording(false);
        }
      };

      recognition.onaudiostart = () => {
        console.log('Audio capture started');
        isRecordingRef.current = true;
        setIsRecording(true);
      };

      recognition.onaudioend = () => {
        console.log('Audio capture ended');
      };

      recognitionRef.current = recognition;
    }
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (message.trim() && !disabled) {
      onSend(message);
      setMessage('');
      messageRef.current = '';
      lastFinalTranscriptRef.current = '';
      voiceCommittedRef.current = '';
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const handleMicClick = () => {
    if (!isSupported || !recognitionRef.current) {
      alert('Speech recognition is not supported in your browser. Please use Chrome, Edge, or Safari.');
      return;
    }

    if (isRecording) {
      isRecordingRef.current = false;
      recognitionRef.current.stop();
      setIsRecording(false);
      lastFinalTranscriptRef.current = '';
      voiceCommittedRef.current = '';
    } else {
      // Start recording; keep any already-typed text as prefix for voice
      voiceCommittedRef.current = messageRef.current.trim();
      lastFinalTranscriptRef.current = '';
      try {
        recognitionRef.current.start();
        // State will be set by onstart handler
      } catch (error) {
        console.error('Error starting speech recognition:', error);
        isRecordingRef.current = false;
        setIsRecording(false);
      }
    }
  };

  return (
    <form className="chat-input-form" onSubmit={handleSubmit}>
      <div className="chat-input-container">
        <textarea
          className="chat-input"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder={placeholder || 'Type a message...'}
          disabled={disabled}
          rows={3}
        />
        <div className="chat-input-actions">
          {isSupported && (
            <button
              type="button"
              className={`chat-mic-button ${isRecording ? 'recording' : ''}`}
              onClick={handleMicClick}
              disabled={disabled}
              title={isRecording ? 'Stop recording' : 'Start voice input'}
            >
              {isRecording ? <MicOff size={18} /> : <Mic size={18} />}
            </button>
          )}
          <button
            type="submit"
            className="chat-send-button"
            disabled={disabled || !message.trim()}
          >
            <Send size={18} />
          </button>
        </div>
      </div>
    </form>
  );
};

export default ChatInput;







